import type { FaceCaptureStepValues } from '../constants';
import type { BffSubmitResponse } from '@/services/rivaas-api';
import type { FailReasonValues } from '@repo/common';

import { useCallback, useState } from 'react';

import { FaceCaptureStep } from '../constants';

import { useAppContext } from '@/providers/app-provider/context';
import { postSubmitHandled } from '@/services/rivaas-api';
import { applyBffSubmitResponse, buildSubmitErrorContext, retrySubmitErrorStep } from '@/utils/submit-error';

export const useUploadFace = () => {
  const { dispatch, state } = useAppContext();
  const [captureStep, setCaptureStep] = useState<FaceCaptureStepValues>(FaceCaptureStep.CAPTURE_SELFIE);
  const [selfie, setSelfie] = useState<Blob | null>(null);
  const [serverErrorFailReason, setServerErrorFailReason] = useState<FailReasonValues | null>(null);

  const [serverErrorMessage, setServerErrorMessage] = useState<string | null>(null);
  const [serverErrorRetryable, setServerErrorRetryable] = useState(false);

  const redirectOptions = {
    unverifiedUrl: state.customerData?.redirect?.unverifiedUrl,
    verifiedUrl: state.customerData?.redirect?.verifiedUrl,
  };

  const uploadSelfie = async (blob: Blob, arrayBuffer: Uint8Array): Promise<BffSubmitResponse> => {
    setServerErrorFailReason(null);
    setServerErrorMessage(null);
    setServerErrorRetryable(false);

    setSelfie(blob);
    setCaptureStep(FaceCaptureStep.PROCESSING);

    // `arrayBuffer` is the DOT `onComplete` DIS liveness-record blob, not a raw JPEG — post it as octet-stream so the BFF routes it to `POST /liveness/records`.
    const data = await postSubmitHandled(new Blob([new Uint8Array(arrayBuffer)], { type: 'application/octet-stream' }));

    if (applyBffSubmitResponse(dispatch, data, redirectOptions)) {
      const errorContext = buildSubmitErrorContext(data);

      setSelfie(null);
      setCaptureStep(FaceCaptureStep.CAPTURE_SELFIE);
      setServerErrorFailReason(errorContext?.failReason ?? null);
      setServerErrorMessage(errorContext?.message ?? null);
      setServerErrorRetryable(errorContext?.retryable ?? false);

      return data;
    }

    return data;
  };

  const clearServerError = useCallback(() => {
    setServerErrorFailReason(null);
    setServerErrorMessage(null);
    setServerErrorRetryable(false);
  }, []);

  const retryCapture = useCallback(() => {
    clearServerError();
    retrySubmitErrorStep(dispatch, state.submitErrorContext, redirectOptions);
  }, [clearServerError, dispatch, redirectOptions, state.submitErrorContext]);

  return {
    captureStep,
    clearServerError,
    retryCapture,
    selfie,
    serverErrorFailReason,
    serverErrorMessage,
    serverErrorRetryable,
    uploadSelfie,
  };
};
